// packages/frontend/src/components/DAMonthlyStats.tsx
import { useEffect, useMemo, useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { makeAuthenticatedApiCall } from '../apiHelper';

// --- Types pour la vue détaillée (un seul mois) ---

interface DADetailRow {
  registrationId: number;
  regPlate: string;
  createdAt: string;
  agencyId: number;
  agencyName: string;
  franchiseId: string | null;
  daFee: number;
  invoiceStatus: string | null;
  invoicingType: string | null;
  clientId: string | null;
  status: string;
}

interface AgencyStats {
  agencyId: number;
  agencyName: string;
  franchiseId: string | null;
  count: number;
  daFee?: number;
  totalValue?: number;
}

interface DAMonthlyStatsData {
  totalDA: number;
  daByAgency: AgencyStats[];
  totalValue: number;
  valueByAgency: AgencyStats[];
  paymentStatus: {
    paid: { count: number; totalValue: number };
    unpaid: { count: number; totalValue: number };
    pendingInvoice: { count: number; totalValue: number };
  };
  daDetails: DADetailRow[];
}

// --- Types pour la vue évolution multi-mois ---

interface DAAgencyMonthData {
  daCount: number;
  unpaidCount: number;
  unpaidValue: number;
  paidCount: number;
  paidValue: number;
  commissions: number;
}

interface DAAgencyEvolution {
  agencyId: number;
  agencyName: string;
  franchiseId: string | null;
  monthlyData: Record<string, DAAgencyMonthData>;
}

interface DAMonthData {
  year: number;
  month: number;
  label: string;
  totalDA: number;
  daFeesTotal: number;
  paid: { count: number; value: number };
  unpaid: { count: number; value: number };
  pending: { count: number; value: number };
}

interface DAMonthlyRangeStats {
  months: DAMonthData[];
  agencies: DAAgencyEvolution[];
}

interface AvailableMonth {
  year: number;
  month: number;
  count: number;
}

const MONTH_NAMES = [
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
];

const stickyCellStyle = {
  background: '#262626',
  fontWeight: 'bold',
  minWidth: 50,
  color: 'white',
  position: 'sticky',
  top: 0,
  zIndex: 100,
};

const stickyFirstColStyle = {
  ...stickyCellStyle,
  left: 0,
  zIndex: 200,
  minWidth: 180,
};

const frozenCellStyle = {
  position: 'sticky',
  left: 0,
  background: 'white',
  zIndex: 50,
  minWidth: 180,
  borderRight: '2px solid #e0e0e0',
};

const getStatusChip = (status: string | null) => {
  if (!status) {
    return <Chip label="En attente de facturation" size="small" sx={{ backgroundColor: '#f5f5f5', color: '#666' }} />;
  }
  switch (status) {
    case 'paid':
      return <Chip label="Payé" size="small" sx={{ backgroundColor: '#c8e6c9', color: '#2e7d32' }} />;
    case 'unpaid':
      return <Chip label="Impayé" size="small" sx={{ backgroundColor: '#ffcdd2', color: '#c62828' }} />;
    default:
      return <Chip label={status} size="small" />;
  }
};

const getDAStatusChip = (status: string) => {
  switch (status) {
    case 'DA_COMPLETED':
      return <Chip label="Terminée" size="small" sx={{ backgroundColor: '#c8e6c9', color: '#2e7d32' }} />;
    case 'DA_PROCESSING':
      return <Chip label="En cours" size="small" sx={{ backgroundColor: '#fff3e0', color: '#e65100' }} />;
    case 'DA_REQUEST':
      return <Chip label="Demande" size="small" sx={{ backgroundColor: '#e3f2fd', color: '#1565c0' }} />;
    default:
      return <Chip label={status} size="small" />;
  }
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

type ViewMode = 'evolution' | 'detail';

const DAMonthlyStats = () => {
  const [availableMonths, setAvailableMonths] = useState<AvailableMonth[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Evolution view state
  const [startMonth, setStartMonth] = useState<string>('');
  const [endMonth, setEndMonth] = useState<string>('');
  const [rangeData, setRangeData] = useState<DAMonthlyRangeStats | null>(null);
  const [isLoadingRange, setIsLoadingRange] = useState<boolean>(false);

  // Detail view state
  const [viewMode, setViewMode] = useState<ViewMode>('evolution');
  const [detailMonth, setDetailMonth] = useState<string>('');
  const [detailData, setDetailData] = useState<DAMonthlyStatsData | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);

  // Fetch available months on mount
  useEffect(() => {
    const fetchAvailableMonths = async () => {
      try {
        const response = await makeAuthenticatedApiCall('get', '/api/da/stats/available-months');
        const data = response.data as { success: boolean; data: AvailableMonth[] };
        if (data.success) {
          const validMonths = data.data.filter((m) => m.year && m.month && m.month >= 1 && m.month <= 12);
          setAvailableMonths(validMonths);
          if (validMonths.length >= 3) {
            setEndMonth(`${validMonths[0].year}-${validMonths[0].month}`);
            setStartMonth(`${validMonths[2].year}-${validMonths[2].month}`);
          } else if (validMonths.length > 0) {
            const last = validMonths[validMonths.length - 1];
            setEndMonth(`${validMonths[0].year}-${validMonths[0].month}`);
            setStartMonth(`${last.year}-${last.month}`);
          }
        }
      } catch (err) {
        console.error('Error fetching DA available months:', err);
        setError('Erreur lors de la récupération des mois disponibles');
      }
    };
    fetchAvailableMonths();
  }, []);

  // Fetch range stats when start/end changes
  useEffect(() => {
    const fetchRangeStats = async () => {
      if (!startMonth || !endMonth) return;

      const [sY, sM] = startMonth.split('-').map(Number);
      const [eY, eM] = endMonth.split('-').map(Number);

      if (sY > eY || (sY === eY && sM > eM)) {
        setError('Le mois de début doit être antérieur au mois de fin');
        setRangeData(null);
        return;
      }

      setIsLoadingRange(true);
      setError(null);

      try {
        const response = await makeAuthenticatedApiCall(
          'get',
          `/api/da/stats/monthly-range?startYear=${sY}&startMonth=${sM}&endYear=${eY}&endMonth=${eM}`,
        );
        const data = response.data as { success: boolean; data: DAMonthlyRangeStats };
        if (data.success) {
          setRangeData(data.data);
        }
      } catch (err) {
        console.error('Error fetching DA range stats:', err);
        setError('Erreur lors de la récupération des statistiques');
      } finally {
        setIsLoadingRange(false);
      }
    };

    fetchRangeStats();
  }, [startMonth, endMonth]);

  // Fetch detail stats when switching to detail view
  useEffect(() => {
    const fetchDetail = async () => {
      if (viewMode !== 'detail' || !detailMonth) return;

      setIsLoadingDetail(true);
      const [year, month] = detailMonth.split('-').map(Number);

      try {
        const response = await makeAuthenticatedApiCall('get', `/api/da/stats/monthly?year=${year}&month=${month}`);
        const data = response.data as { success: boolean; data: DAMonthlyStatsData };
        if (data.success) {
          setDetailData(data.data);
        }
      } catch (err) {
        console.error('Error fetching DA detail stats:', err);
        setError('Erreur lors de la récupération du détail');
      } finally {
        setIsLoadingDetail(false);
      }
    };

    fetchDetail();
  }, [viewMode, detailMonth]);

  const handleClickMonth = (year: number, month: number) => {
    setDetailMonth(`${year}-${month}`);
    setViewMode('detail');
  };

  const handleBackToEvolution = () => {
    setViewMode('evolution');
    setDetailData(null);
  };

  const periodTotals = useMemo(() => {
    if (!rangeData) return null;
    return rangeData.months.reduce(
      (acc, m) => ({
        totalDA: acc.totalDA + m.totalDA,
        daFeesTotal: acc.daFeesTotal + m.daFeesTotal,
        paidCount: acc.paidCount + m.paid.count,
        paidValue: acc.paidValue + m.paid.value,
        unpaidCount: acc.unpaidCount + m.unpaid.count,
        unpaidValue: acc.unpaidValue + m.unpaid.value,
        pendingCount: acc.pendingCount + m.pending.count,
        pendingValue: acc.pendingValue + m.pending.value,
      }),
      { totalDA: 0, daFeesTotal: 0, paidCount: 0, paidValue: 0, unpaidCount: 0, unpaidValue: 0, pendingCount: 0, pendingValue: 0 },
    );
  }, [rangeData]);

  const agenciesSortedByUnpaid = useMemo(() => {
    if (!rangeData) return [];
    return [...rangeData.agencies].sort((a, b) => {
      const totalUnpaidA = Object.values(a.monthlyData).reduce((sum, m) => sum + m.unpaidCount, 0);
      const totalUnpaidB = Object.values(b.monthlyData).reduce((sum, m) => sum + m.unpaidCount, 0);
      return totalUnpaidB - totalUnpaidA;
    });
  }, [rangeData]);

  const agenciesWithUnpaid = useMemo(() => {
    return agenciesSortedByUnpaid.filter((a) => Object.values(a.monthlyData).some((m) => m.unpaidCount > 0));
  }, [agenciesSortedByUnpaid]);

  if (error && !rangeData && !detailData && viewMode === 'evolution' && !startMonth) {
    return (
      <Container maxWidth={false} sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  // ======================= DETAIL VIEW =======================
  if (viewMode === 'detail') {
    const [dYear, dMonth] = detailMonth.split('-').map(Number);
    const monthLabel = `${MONTH_NAMES[dMonth - 1]} ${dYear}`;

    return (
      <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
          Statistiques DA Mensuelles
        </Typography>

        <Box sx={{ mb: 4 }}>
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToEvolution}
            sx={{
              color: '#464646',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { backgroundColor: 'rgba(100, 181, 246, 0.1)' },
            }}
          >
            Retour à l'évolution
          </Button>
          <Typography variant="h5" sx={{ mt: 1, fontWeight: 600, color: '#262626' }}>
            Détail — {monthLabel}
          </Typography>
        </Box>

        {isLoadingDetail ? (
          <Grid container direction="column" justifyContent="center" alignItems="center" sx={{ mt: 4 }}>
            <Typography sx={{ mb: 2 }}>Chargement du détail...</Typography>
            <CircularProgress size={70} />
          </Grid>
        ) : detailData ? (
          <>
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={4}>
                <Card sx={{ backgroundColor: 'rgba(100, 181, 246, 0.15)', height: '100%', border: '1px solid #64b5f6' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#262626' }}>Volume</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#262626', mt: 1 }}>{detailData.totalDA}</Typography>
                    <Typography variant="body2" sx={{ color: '#464646' }}>DA créées ce mois</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ backgroundColor: 'rgba(66, 165, 245, 0.15)', height: '100%', border: '1px solid #42a5f5' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#262626' }}>Valeur</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#262626', mt: 1 }}>{detailData.totalValue.toFixed(2)} €</Typography>
                    <Typography variant="body2" sx={{ color: '#464646' }}>Montant total (frais DA payés)</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ backgroundColor: 'rgba(30, 136, 229, 0.12)', height: '100%', border: '1px solid #1e88e5' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#262626' }}>Statut de paiement</Typography>
                    <Box sx={{ mt: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: '#2e7d32' }}>Payé :</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                          {detailData.paymentStatus.paid.count} ({detailData.paymentStatus.paid.totalValue.toFixed(2)} €)
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: '#FF6060' }}>Impayé :</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#FF6060' }}>
                          {detailData.paymentStatus.unpaid.count} ({detailData.paymentStatus.unpaid.totalValue.toFixed(2)} €)
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ color: '#464646' }}>En attente :</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#464646' }}>
                          {detailData.paymentStatus.pendingInvoice.count} ({detailData.paymentStatus.pendingInvoice.totalValue.toFixed(2)} €)
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Box sx={{ mb: 4 }}>
              {/* Volume by Agency */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Volume par agence</Typography>
              <TableContainer component={Paper} sx={{ mb: 4 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={stickyCellStyle}>Agence</TableCell>
                      <TableCell sx={stickyCellStyle}>Franchise</TableCell>
                      <TableCell sx={stickyCellStyle} align="right">Nombre de DA</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detailData.daByAgency.map((agency) => (
                      <TableRow key={agency.agencyId}>
                        <TableCell>{agency.agencyName}</TableCell>
                        <TableCell>{agency.franchiseId || '-'}</TableCell>
                        <TableCell align="right">{agency.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Valeur par agence (avec frais DA par franchise)</Typography>
              <TableContainer component={Paper} sx={{ mb: 4 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={stickyCellStyle}>Agence</TableCell>
                      <TableCell sx={stickyCellStyle}>Franchise</TableCell>
                      <TableCell sx={stickyCellStyle} align="right">Nombre de DA</TableCell>
                      <TableCell sx={stickyCellStyle} align="right">Frais DA unitaire</TableCell>
                      <TableCell sx={stickyCellStyle} align="right">Valeur totale</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detailData.valueByAgency.map((agency) => (
                      <TableRow key={agency.agencyId}>
                        <TableCell>{agency.agencyName}</TableCell>
                        <TableCell>{agency.franchiseId || '-'}</TableCell>
                        <TableCell align="right">{agency.count}</TableCell>
                        <TableCell align="right">{(agency.daFee || 6).toFixed(2)} €</TableCell>
                        <TableCell align="right">{(agency.totalValue || 0).toFixed(2)} €</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Détail des DA ({detailData.daDetails.length})</Typography>
              <TableContainer component={Paper} sx={{ mb: 4 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={stickyCellStyle}>Immatriculation</TableCell>
                      <TableCell sx={stickyCellStyle}>ID</TableCell>
                      <TableCell sx={stickyCellStyle}>Date création</TableCell>
                      <TableCell sx={stickyCellStyle}>Agence</TableCell>
                      <TableCell sx={stickyCellStyle}>Franchise</TableCell>
                      <TableCell sx={stickyCellStyle} align="right">Frais DA</TableCell>
                      <TableCell sx={stickyCellStyle}>Statut DA</TableCell>
                      <TableCell sx={stickyCellStyle}>Type facturation</TableCell>
                      <TableCell sx={stickyCellStyle}>Statut paiement</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detailData.daDetails.map((da) => (
                      <TableRow
                        key={da.registrationId}
                        sx={{
                          backgroundColor:
                            da.invoiceStatus === 'paid'
                              ? 'rgba(200, 230, 201, 0.3)'
                              : da.invoiceStatus === 'unpaid'
                                ? 'rgba(255, 205, 210, 0.3)'
                                : 'inherit',
                        }}
                      >
                        <TableCell>{da.regPlate || '-'}</TableCell>
                        <TableCell>{da.registrationId}</TableCell>
                        <TableCell>{formatDate(da.createdAt)}</TableCell>
                        <TableCell>{da.agencyName}</TableCell>
                        <TableCell>{da.franchiseId || '-'}</TableCell>
                        <TableCell align="right">{da.daFee.toFixed(2)} €</TableCell>
                        <TableCell>{getDAStatusChip(da.status)}</TableCell>
                        <TableCell>{da.invoicingType || '-'}</TableCell>
                        <TableCell>{getStatusChip(da.invoiceStatus)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </>
        ) : null}
      </Container>
    );
  }

  // ======================= EVOLUTION VIEW =======================
  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
        Statistiques DA Mensuelles — Évolution
      </Typography>

      {/* Month range selectors */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 220 }}>
          <InputLabel className="inputElement">Mois de début</InputLabel>
          <Select
            className="inputElement"
            value={startMonth}
            onChange={(e: SelectChangeEvent<string>) => setStartMonth(e.target.value)}
            label="Mois de début"
          >
            {availableMonths.map((m) => (
              <MenuItem key={`start-${m.year}-${m.month}`} value={`${m.year}-${m.month}`}>
                {MONTH_NAMES[m.month - 1]} {m.year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 220 }}>
          <InputLabel className="inputElement">Mois de fin</InputLabel>
          <Select
            className="inputElement"
            value={endMonth}
            onChange={(e: SelectChangeEvent<string>) => setEndMonth(e.target.value)}
            label="Mois de fin"
          >
            {availableMonths.map((m) => (
              <MenuItem key={`end-${m.year}-${m.month}`} value={`${m.year}-${m.month}`}>
                {MONTH_NAMES[m.month - 1]} {m.year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic' }}>
          Cliquez sur un mois dans les tableaux pour voir le détail
        </Typography>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>{error}</Alert>
      )}

      {isLoadingRange ? (
        <Grid container direction="column" justifyContent="center" alignItems="center" sx={{ mt: 4 }}>
          <Typography sx={{ mb: 2 }}>Chargement des statistiques...</Typography>
          <CircularProgress size={70} />
        </Grid>
      ) : rangeData && periodTotals ? (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={3}>
              <Card sx={{ backgroundColor: 'rgba(100, 181, 246, 0.15)', height: '100%', border: '1px solid #64b5f6' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#262626' }}>Volume total</Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#262626', mt: 1 }}>{periodTotals.totalDA}</Typography>
                  <Typography variant="body2" sx={{ color: '#464646' }}>DA sur la période</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ backgroundColor: 'rgba(66, 165, 245, 0.15)', height: '100%', border: '1px solid #42a5f5' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#262626' }}>Commissions facturées</Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#262626', mt: 1 }}>{periodTotals.daFeesTotal.toFixed(0)} €</Typography>
                  <Typography variant="body2" sx={{ color: '#464646' }}>Total frais DA facturés</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ backgroundColor: 'rgba(200, 230, 201, 0.3)', height: '100%', border: '1px solid #81c784' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>Payé</Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#2e7d32', mt: 1 }}>{periodTotals.paidValue.toFixed(0)} €</Typography>
                  <Typography variant="body2" sx={{ color: '#464646' }}>{periodTotals.paidCount} DA payées</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ backgroundColor: 'rgba(255, 205, 210, 0.3)', height: '100%', border: '1px solid #e57373' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#c62828' }}>Impayé</Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#c62828', mt: 1 }}>{periodTotals.unpaidValue.toFixed(0)} €</Typography>
                  <Typography variant="body2" sx={{ color: '#464646' }}>{periodTotals.unpaidCount} DA impayées</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ mb: 4 }}>
            {/* Évolution des impayés par agence */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Évolution des impayés par agence ({agenciesWithUnpaid.length} agences)
            </Typography>
            <Box sx={{ mb: 4 }}>
                {agenciesWithUnpaid.length === 0 ? (
                  <Alert severity="success">Aucun impayé sur la période sélectionnée</Alert>
                ) : (
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ ...stickyFirstColStyle, borderRight: '2px solid #555' }}>Agence</TableCell>
                          {rangeData.months.map((m) => (
                            <TableCell
                              key={`unpaid-h-${m.year}-${m.month}`}
                              sx={{ ...stickyCellStyle, minWidth: 100, cursor: 'pointer' }}
                              align="center"
                              onClick={() => handleClickMonth(m.year, m.month)}
                            >
                              <Tooltip title="Cliquer pour voir le détail">
                                <span>{m.label}</span>
                              </Tooltip>
                            </TableCell>
                          ))}
                          <TableCell sx={{ ...stickyCellStyle, minWidth: 80, backgroundColor: '#1a1a1a' }} align="center">
                            Total
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {agenciesWithUnpaid.map((agency) => {
                          const totalUnpaid = Object.values(agency.monthlyData).reduce((s, m) => s + m.unpaidCount, 0);
                          const totalUnpaidVal = Object.values(agency.monthlyData).reduce((s, m) => s + m.unpaidValue, 0);
                          return (
                            <TableRow key={agency.agencyId}>
                              <TableCell sx={frozenCellStyle}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{agency.agencyName}</Typography>
                                <Typography variant="caption" sx={{ color: '#666' }}>{agency.franchiseId || '-'}</Typography>
                              </TableCell>
                              {rangeData.months.map((m) => {
                                const key = `${m.year}-${m.month}`;
                                const data = agency.monthlyData[key];
                                const hasUnpaid = data && data.unpaidCount > 0;
                                return (
                                  <TableCell
                                    key={`unpaid-${agency.agencyId}-${key}`}
                                    align="center"
                                    sx={{
                                      backgroundColor: hasUnpaid ? 'rgba(255, 205, 210, 0.4)' : 'inherit',
                                      cursor: 'pointer',
                                      '&:hover': { backgroundColor: 'rgba(100, 181, 246, 0.2)' },
                                    }}
                                    onClick={() => handleClickMonth(m.year, m.month)}
                                  >
                                    {data && data.unpaidCount > 0 ? (
                                      <>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#c62828' }}>
                                          {data.unpaidCount}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#c62828' }}>
                                          {data.unpaidValue.toFixed(0)} €
                                        </Typography>
                                      </>
                                    ) : (
                                      <Typography variant="body2" sx={{ color: '#ccc' }}>-</Typography>
                                    )}
                                  </TableCell>
                                );
                              })}
                              <TableCell
                                align="center"
                                sx={{ backgroundColor: totalUnpaid > 0 ? 'rgba(255, 205, 210, 0.6)' : 'inherit', fontWeight: 700 }}
                              >
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#c62828' }}>{totalUnpaid}</Typography>
                                <Typography variant="caption" sx={{ color: '#c62828' }}>{totalUnpaidVal.toFixed(0)} €</Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
            </Box>

            {/* Volume par agence - évolution */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Volume par agence — Évolution ({rangeData.agencies.length} agences)
            </Typography>
            <Box sx={{ mb: 4 }}>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...stickyFirstColStyle, borderRight: '2px solid #555' }}>Agence</TableCell>
                        {rangeData.months.map((m) => (
                          <TableCell
                            key={`vol-h-${m.year}-${m.month}`}
                            sx={{ ...stickyCellStyle, minWidth: 80, cursor: 'pointer' }}
                            align="center"
                            onClick={() => handleClickMonth(m.year, m.month)}
                          >
                            <Tooltip title="Cliquer pour voir le détail">
                              <span>{m.label}</span>
                            </Tooltip>
                          </TableCell>
                        ))}
                        <TableCell sx={{ ...stickyCellStyle, minWidth: 80, backgroundColor: '#1a1a1a' }} align="center">
                          Total
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rangeData.agencies.map((agency) => {
                        const totalDA = Object.values(agency.monthlyData).reduce((s, m) => s + m.daCount, 0);
                        return (
                          <TableRow key={agency.agencyId}>
                            <TableCell sx={frozenCellStyle}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{agency.agencyName}</Typography>
                              <Typography variant="caption" sx={{ color: '#666' }}>{agency.franchiseId || '-'}</Typography>
                            </TableCell>
                            {rangeData.months.map((m) => {
                              const key = `${m.year}-${m.month}`;
                              const data = agency.monthlyData[key];
                              return (
                                <TableCell
                                  key={`vol-${agency.agencyId}-${key}`}
                                  align="center"
                                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(100, 181, 246, 0.2)' } }}
                                  onClick={() => handleClickMonth(m.year, m.month)}
                                >
                                  <Typography variant="body2" sx={{ fontWeight: data && data.daCount > 0 ? 600 : 400, color: data && data.daCount > 0 ? '#262626' : '#ccc' }}>
                                    {data ? data.daCount : 0}
                                  </Typography>
                                </TableCell>
                              );
                            })}
                            <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: 'rgba(100, 181, 246, 0.1)' }}>
                              {totalDA}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Total row */}
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ ...frozenCellStyle, backgroundColor: '#f5f5f5' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>TOTAL</Typography>
                        </TableCell>
                        {rangeData.months.map((m) => (
                          <TableCell key={`vol-total-${m.year}-${m.month}`} align="center">
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{m.totalDA}</Typography>
                          </TableCell>
                        ))}
                        <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: 'rgba(100, 181, 246, 0.2)' }}>
                          {periodTotals.totalDA}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
            </Box>

            {/* Rentabilité - Commissions DA */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Rentabilité — Commissions DA
            </Typography>
            <Box sx={{ mb: 4 }}>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={stickyCellStyle}>Mois</TableCell>
                        <TableCell sx={stickyCellStyle} align="right">DA</TableCell>
                        <TableCell sx={stickyCellStyle} align="right">Commissions facturées</TableCell>
                        <TableCell sx={stickyCellStyle} align="right">Payé</TableCell>
                        <TableCell sx={stickyCellStyle} align="right">Impayé</TableCell>
                        <TableCell sx={stickyCellStyle} align="right">En attente</TableCell>
                        <TableCell sx={stickyCellStyle} align="right">Taux encaissement</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rangeData.months.map((m) => {
                        const encaissementRate = m.daFeesTotal > 0 ? (m.paid.value / m.daFeesTotal) * 100 : 0;
                        return (
                          <TableRow
                            key={`comm-${m.year}-${m.month}`}
                            sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(100, 181, 246, 0.1)' } }}
                            onClick={() => handleClickMonth(m.year, m.month)}
                          >
                            <TableCell sx={{ fontWeight: 600 }}>{m.label}</TableCell>
                            <TableCell align="right">{m.totalDA}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{m.daFeesTotal.toFixed(2)} €</TableCell>
                            <TableCell align="right" sx={{ color: '#2e7d32' }}>
                              {m.paid.value.toFixed(2)} € ({m.paid.count})
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#c62828', fontWeight: m.unpaid.count > 0 ? 700 : 400 }}>
                              {m.unpaid.count > 0 ? `${m.unpaid.value.toFixed(2)} € (${m.unpaid.count})` : '-'}
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#666' }}>
                              {m.pending.count > 0 ? `${m.pending.value.toFixed(2)} € (${m.pending.count})` : '-'}
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${encaissementRate.toFixed(0)}%`}
                                size="small"
                                sx={{
                                  backgroundColor: encaissementRate >= 95 ? '#c8e6c9' : encaissementRate >= 80 ? '#fff3e0' : '#ffcdd2',
                                  color: encaissementRate >= 95 ? '#2e7d32' : encaissementRate >= 80 ? '#e65100' : '#c62828',
                                  fontWeight: 700,
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Total row */}
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 700 }}>TOTAL</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{periodTotals.totalDA}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{periodTotals.daFeesTotal.toFixed(2)} €</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                          {periodTotals.paidValue.toFixed(2)} € ({periodTotals.paidCount})
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#c62828' }}>
                          {periodTotals.unpaidValue.toFixed(2)} € ({periodTotals.unpaidCount})
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#666' }}>
                          {periodTotals.pendingValue.toFixed(2)} € ({periodTotals.pendingCount})
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${periodTotals.daFeesTotal > 0 ? ((periodTotals.paidValue / periodTotals.daFeesTotal) * 100).toFixed(0) : 0}%`}
                            size="small"
                            sx={{ fontWeight: 700, backgroundColor: '#e3f2fd', color: '#1565c0' }}
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
            </Box>
          </Box>
        </>
      ) : null}
    </Container>
  );
};

export default DAMonthlyStats;
